import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Image, Modal, Table, TablePaginationConfig } from "antd";
import styled from "styled-components";
import { TParamsUser, editUser } from "services/UserManagement";
import { User } from "services/auth";
import { SorterResult } from "antd/es/table/interface";
import moment from "moment";
import { deleteReportApi, getUserReportsApi } from "services/user-report";
import { API_URL } from "configAxios";

const initFilter = {
  page: 1,
  limit: 10,
  total: 0,
};

type Report = {
  createdAt: string;
  description: string;
  id: number;
  photo: string;
  reason: string;
  reported: User;
  user: User;
};

const UserReportManagement = () => {
  const [data, setData] = useState<Report[]>([]);
  const [params, setParams] = useState<TParamsUser>(initFilter);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async (params?: TParamsUser) => {
    setIsLoading(true);
    try {
      const { data } = await getUserReportsApi(params);
      setData(data.reports);
      setParams((pre) => ({
        ...pre,
        total: data.total,
        page: params?.page ?? pre.page,
      }));
    } catch (error: any) {
      Modal.error({
        content: error.response?.data.message || "Fetch data failed",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBlock = useCallback(
    async (report: Report) => {
      try {
        await editUser(report.reported.id, {
          isActive: false,
        } as User);
        await deleteReportApi(report.id);
        Modal.success({ content: "Block user success!" });
        fetchData(params);
      } catch (error: any) {
        Modal.error({
          content: error.response?.data.message || "Block failed",
        });
      }
    },
    [fetchData, params]
  );

  const handleDelete = useCallback(
    (id: number) => {
      Modal.confirm({
        title: "Are you sure you want to delete this report?",
        content: "This action cannot be undone.",
        onOk: async () => {
          try {
            await deleteReportApi(id);
            Modal.success({ content: "Delete report success!" });
            fetchData(params);
          } catch (error: any) {
            Modal.error({
              content: error.response?.data.message || "Delete failed",
            });
          }
        },
      });
    },
    [fetchData, params]
  );

  const handleChangeTable = useCallback(
    (
      { current, pageSize }: TablePaginationConfig,
      _: any,
      { order, field }: SorterResult
    ) => {
      if (!current || !pageSize) return;
      fetchData({
        ...params,
        page: current!,
        limit: pageSize!,
        order: order ? (order === "ascend" ? "asc" : "desc") : "",
        sortBy: (field as string) || "",
      });
    },
    [fetchData, params]
  );

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: ({ firstName, lastName }: User) => (
        <p>{`${firstName || ""} ${lastName || ""}`}</p>
      ),
    },
    {
      title: "User Reported",
      dataIndex: "reported",
      key: "reported",
      render: ({ firstName, lastName }: User) => (
        <p>{`${firstName || ""} ${lastName || ""}`}</p>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Photo",
      dataIndex: "photo",
      key: "photo",
      render: (text: string) => (
        <Image
          src={`${API_URL}/api/uploads/${text}`}
          alt="avatar user"
          style={{ width: 50, height: 50 }}
        />
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text: string) => <p>{moment(text).format("MM/DD/YYYY")}</p>,
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: Report) => (
        <Flex gap="small">
          <Button danger type="primary" onClick={() => handleBlock(record)}>
            Block
          </Button>
          <Button onClick={() => handleDelete(record.id)} danger>
            Delete
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <WrapperUserManagement>
      <h1>User Report</h1>
      <Table<Report>
        loading={isLoading}
        dataSource={data}
        onChange={handleChangeTable as any}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: params.total,
          showSizeChanger: false,
        }}
        columns={columns}
      />
    </WrapperUserManagement>
  );
};

export default UserReportManagement;

const WrapperUserManagement = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 20px;
  &.ant-pagination {
    margin: 8px 0px !important;
  }
  > h1 {
    margin: 0;
  }
`;
